import React from "react";
import { CheckCircle, XCircle, EyeOff } from "lucide-react";
import api from "../../api/client";

const severityBadge = {
  critical: "bg-red-900/30 text-red-400 border-red-800/40",
  high: "bg-amber-900/30 text-amber-400 border-amber-800/40",
  medium: "bg-blue-900/30 text-blue-400 border-blue-800/40",
};

const statusBadge = {
  open: "bg-dark-50 text-gray-300",
  resolved: "bg-green-900/20 text-green-400",
  dismissed: "bg-gray-800 text-gray-500",
};

export default function HealthFindingsTable({
  findings,
  siteId,
  onUpdate,
  severityFilter,
  setSeverityFilter,
  statusFilter,
  setStatusFilter,
}) {
  const handleAction = async (id, newStatus) => {
    try {
      await api.patch(`/websites/${siteId}/health/findings/${id}/`, {
        status: newStatus,
      });
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Failed to update finding", err);
    }
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {["all", "critical", "high", "medium"].map((s) => (
          <button
            key={s}
            onClick={() => setSeverityFilter(s === "all" ? "" : s)}
            className={`text-xs px-3 py-1 rounded-full border ${
              (s === "all" && !severityFilter) || severityFilter === s
                ? "bg-copper/20 border-copper text-copper"
                : "bg-dark-300 border-dark-50 text-gray-400 hover:border-gray-600"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span className="mx-2 border-l border-dark-50" />
        {["all", "open", "resolved", "dismissed"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s === "all" ? "" : s)}
            className={`text-xs px-3 py-1 rounded-full border ${
              (s === "all" && !statusFilter) || statusFilter === s
                ? "bg-copper/20 border-copper text-copper"
                : "bg-dark-300 border-dark-50 text-gray-400 hover:border-gray-600"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-dark-50">
              <th className="text-left py-2 px-3">Severity</th>
              <th className="text-left py-2 px-3">Check</th>
              <th className="text-left py-2 px-3">URL</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Last Seen</th>
              <th className="text-right py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {findings.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-gray-600 py-8">
                  No findings match the current filters.
                </td>
              </tr>
            )}
            {findings.map((f) => (
              <tr key={f.id} className="border-b border-dark-50 hover:bg-dark-300/50">
                <td className="py-2 px-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded border ${
                      severityBadge[f.severity] || ""
                    }`}
                  >
                    {f.severity}
                  </span>
                </td>
                <td className="py-2 px-3">
                  <div className="font-medium text-gray-200">{f.headline}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{f.check_type}</div>
                </td>
                <td className="py-2 px-3 max-w-[300px]">
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-copper hover:underline truncate block"
                    title={f.url}
                  >
                    {f.url.replace(/^https?:\/\//, "").slice(0, 60)}
                  </a>
                </td>
                <td className="py-2 px-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      statusBadge[f.status] || ""
                    }`}
                  >
                    {f.status}
                  </span>
                </td>
                <td className="py-2 px-3 text-xs text-gray-500">
                  {f.last_seen
                    ? new Date(f.last_seen).toLocaleDateString()
                    : "—"}
                </td>
                <td className="py-2 px-3 text-right">
                  {f.status === "open" && (
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => handleAction(f.id, "resolved")}
                        className="p-1 rounded hover:bg-green-900/30 text-green-500"
                        title="Mark resolved"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleAction(f.id, "dismissed")}
                        className="p-1 rounded hover:bg-gray-700 text-gray-500"
                        title="Dismiss"
                      >
                        <EyeOff className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
